import React from 'react';
import { motion } from 'framer-motion';
import { OpeningHours, availableServices } from '../../types/business';

interface OpeningHoursManagerProps {
  openingHours: OpeningHours;
  selectedServices: string[];
  editMode: boolean;
  loading: boolean;
  onHoursChange: (day: string, field: string, value: string | boolean) => void;
  onServicesChange: (service: string) => void;
  onSave: () => void;
  onEditToggle: () => void;
  onCancel: () => void;
  onSaveServices: () => void; // NEW PROP
}

export const OpeningHoursManager: React.FC<OpeningHoursManagerProps> = ({
  openingHours,
  selectedServices,
  editMode,
  loading,
  onHoursChange,
  onServicesChange,
  onSave,
  onEditToggle,
  onCancel,
  onSaveServices
}) => {
  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const timeSlots = [
    '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
    '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
  ];

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="space-y-8">
      {/* Opening Hours Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Opening Hours</h3>
          {!editMode && (
            <button
              onClick={onEditToggle}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit Hours
            </button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-4">
            {daysOfWeek.map((day) => {
              const dayHours = openingHours[day.key as keyof OpeningHours];
              return (
                <div key={day.key} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{day.label}</h4>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={!dayHours.closed}
                        onChange={(e) => onHoursChange(day.key, 'closed', !e.target.checked)}
                        className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Open</span>
                    </label>
                  </div>

                  {!dayHours.closed && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Opening Time
                        </label>
                        <select
                          value={dayHours.open}
                          onChange={(e) => onHoursChange(day.key, 'open', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {formatTime(time)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Closing Time
                        </label>
                        <select
                          value={dayHours.close}
                          onChange={(e) => onHoursChange(day.key, 'close', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {formatTime(time)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {dayHours.closed && (
                    <p className="text-sm text-gray-500 italic">Closed</p>
                  )}
                </div>
              );
            })}

            <div className="flex space-x-4 pt-4">
              <button
                onClick={onSave}
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : "Save Hours"}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {daysOfWeek.map((day) => {
              const dayHours = openingHours[day.key as keyof OpeningHours];
              return (
                <div key={day.key} className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-900">{day.label}</span>
                  <span className="text-gray-600">
                    {dayHours.closed
                      ? "Closed"
                      : `${formatTime(dayHours.open)} - ${formatTime(dayHours.close)}`
                    }
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Services Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Services Offered</h3>
          <button
            onClick={onSaveServices}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Services
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableServices.map((service) => (
            <label key={service} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedServices.includes(service)}
                onChange={() => onServicesChange(service)}
                className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900">{service}</span>
            </label>
          ))}
        </div>


        {selectedServices.length === 0 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700">
              Please select at least one service to help customers find your business.
            </p>
          </div>
        )}

        {selectedServices.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Services:</h4>
            <div className="flex flex-wrap gap-2">
              {selectedServices.map((service) => (
                <span
                  key={service}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {service}
                  <button
                    onClick={() => onServicesChange(service)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};